import * as errors from '@attain-apps/errors'
import { TypeChecker } from '@attain-apps/type-guards'

import { Range } from './range'

export class Multirange<in out T extends NonNullable<unknown>> {
  /**
   * Tries to create a {@link Multirange} by parsing a string value in the format of a postgres multirange.
   */
  public static fromPostgres<T extends NonNullable<unknown>>(
    pgStr: string,
    parseValue: (val: string) => T,
  ): Multirange<T>
  public static fromPostgres(pgStr: string): Multirange<string>
  public static fromPostgres(
    pgStr: string,
    parseValue?: (val: string) => NonNullable<unknown>,
  ): Multirange<NonNullable<unknown>>
  public static fromPostgres(
    pgStr: string,
    parseValue?: (val: string) => NonNullable<unknown>,
  ): Multirange<any> {
    pgStr = pgStr.trim()
    // First we pull out the ranges from in between { and }
    const multiRangeRegexp = /^\{(?<ranges>.*)\}$/
    let rangesStr = multiRangeRegexp.exec(pgStr)?.groups?.ranges
    if (TypeChecker.isNullish(rangesStr)) {
      throw new errors.ValidationError({
        reason: "Multiranges must start with '{' and end with '}'",
        type: 'multirange format',
        value: pgStr,
      })
    }

    // Then we split the ranges apart so we can parse each one individually
    rangesStr = rangesStr.trim()
    if (rangesStr === '') {
      return Multirange.empty()
    }
    // NOTE: this is not very strict. It just looks for each instance of [ or (
    // followed by a ] or )
    const rangeRegexp = /(?:\[|\().*?(?:\]|\))/g
    // NOTE: 'g' modifier ensures we get a list from 'match'
    const rangeStrs = rangesStr.match(rangeRegexp)
    if (TypeChecker.isNullish(rangeStrs)) {
      throw new errors.ValidationError({
        reason:
          'Multiranges must contain a comma-separated list of ranges (or be empty)',
        type: 'multirange format',
        value: pgStr,
      })
    }

    // Here is where we parse each range individually
    return new Multirange(
      rangeStrs.map(rangeStr => Range.fromPostgres(rangeStr, parseValue)),
    )
  }
  public static everything<T extends NonNullable<unknown>>(): Multirange<T> {
    return new Multirange(Range.everything<T>())
  }
  public static empty<T extends NonNullable<unknown>>(): Multirange<T> {
    return new Multirange(Range.empty<T>())
  }

  /** Sorted in ascending order */
  public get ranges(): readonly Range<T>[] {
    return this._ranges
  }
  /**
   * @see {@link Range.isEmpty}
   */
  public get isEmpty(): boolean {
    // NOTE: empty ranges are filtered out from this._ranges
    return this._ranges.length === 0
  }
  /**
   * @see {@link Range.includesEverything}
   */
  public get includesEverything(): boolean {
    // NOTE: when any range includesEverything, we end up with only one range
    // that includesEverything
    return !this.isEmpty && this._ranges[0].includesEverything
  }

  private readonly _ranges: readonly Range<T>[]

  constructor(...ranges: Range<T>[])
  constructor(
    source?:
      | Multirange.constructor.Params<T>
      | Range<T>
      | readonly Range<T>[]
      | Multirange<T>,
  )
  constructor(
    firstParam?:
      | Multirange.constructor.Params<T>
      | Range<T>
      | readonly Range<T>[]
      | Multirange<T>,
    ...otherRanges: Range<T>[]
  ) {
    const ranges: readonly Range<T>[] = TypeChecker.isReadonlyArray(firstParam)
      ? firstParam
      : firstParam instanceof Range
        ? [firstParam, ...otherRanges]
        : (firstParam?.ranges ?? [])
    this._ranges = _cleanUpRanges(ranges)
  }

  public contains(val: T | Range<T> | Multirange<T>): boolean {
    if (this.isEmpty) {
      // NOTE: we attempt to match Postgres' handling of empty multiranges
      return false
    }
    const vals: readonly (T | Range<T>)[] =
      val instanceof Multirange ? val._ranges : [val]
    return vals.every(_val => this._ranges.some(range => range.contains(_val)))
  }
  public overlaps(range: Range<T> | Multirange<T>): boolean {
    if (this.isEmpty || range.isEmpty) {
      // NOTE: we attempt to match Postgres' handling of empty multiranges
      return false
    }
    const ranges: readonly Range<T>[] =
      range instanceof Multirange ? range._ranges : [range]
    return ranges.some(targetRange =>
      this._ranges.some(internalRange => internalRange.overlaps(targetRange)),
    )
  }
  public isStrictlyLeftOf(range: Range<T> | Multirange<T>): boolean {
    if (this.isEmpty || range.isEmpty) {
      // NOTE: we attempt to match Postgres' handling of empty multiranges
      return false
    }
    const comparing: Range<T> =
      range instanceof Multirange ? range._ranges[0] : range
    return this._ranges[this._ranges.length - 1].isStrictlyLeftOf(comparing)
  }
  public isStrictlyRightOf(range: Range<T> | Multirange<T>): boolean {
    if (this.isEmpty || range.isEmpty) {
      // NOTE: we attempt to match Postgres' handling of empty multiranges
      return false
    }
    const comparing: Range<T> =
      range instanceof Multirange
        ? range._ranges[range._ranges.length - 1]
        : range
    return this._ranges[0].isStrictlyRightOf(comparing)
  }

  public union(range: Range<T> | Multirange<T>): Multirange<T> {
    return new Multirange({
      ranges: [
        ...this._ranges,
        ...(range instanceof Multirange ? range._ranges : [range]),
      ],
    })
  }
  public intersection(range: Range<T> | Multirange<T>): Multirange<T> {
    return new Multirange({
      ranges: (range instanceof Multirange ? range._ranges : [range])
        .map(targetRange =>
          this._ranges
            .find(internalRange => internalRange.overlaps(targetRange))
            ?.intersection(targetRange),
        )
        .filter(
          (targetRange: any): targetRange is Range<T> =>
            targetRange instanceof Range,
        ),
    })
  }

  public toJSON(): Multirange.JSON<T> {
    return {
      $multirange: this.ranges.map(range => range.toJSON()),
    }
  }

  public toPostgres(endpointSerializer?: (val: T) => string): string {
    return `{${this._ranges
      .map(range => range.toPostgres(endpointSerializer))
      .join(', ')}}`
  }

  public valueOf(): number {
    // NOTE: -Infinity matches the behavior of an empty Range
    return this.isEmpty ? -Infinity : this._ranges[0].valueOf()
  }
}
export namespace Multirange {
  export namespace constructor {
    export interface Params<T extends NonNullable<unknown>> {
      /** @default [] */
      ranges?: readonly Range<T>[]
    }
  }

  export interface JSON<T = unknown> {
    $multirange: readonly Range.JSON<T>[]
  }

  export const isMultirange: TypeChecker<Multirange<NonNullable<unknown>>> =
    TypeChecker.Factory.instance(Multirange)

  export function getTypeChecker<T extends NonNullable<unknown>>(
    isT: TypeChecker<T>,
  ): TypeChecker<Multirange<T>> {
    const isRange = Range.getTypeChecker(isT)
    return TypeChecker.Factory.custom(
      (val: unknown): val is Multirange<T> =>
        isMultirange(val) && val.ranges.every(range => isRange(range)),
      {
        get typeDescription(): string {
          return `Multirange<${isT.typeDescription}>`
        },
      },
    )
  }
}

/**
 * Returns sorted, simplified ranges for a multirange
 *
 * NOTE: MUST RETURN ASCENDING ORDER
 */
function _cleanUpRanges<T extends NonNullable<unknown>>(
  ranges: readonly Range<T>[],
): Range<T>[] {
  const finalRanges: Range<T>[] = []
  for (const range of [...ranges].sort(
    (range1, range2) => range1.valueOf() - range2.valueOf(),
  )) {
    if (range.isEmpty) {
      // Empty ranges add nothing to the multirange, so we can just skip them
      continue
    }
    if (range.includesEverything) {
      // If the current range includes everything, then the multirange will just
      // contain this one range that includes everything
      return [range]
    }
    if (
      !finalRanges.length ||
      !finalRanges[finalRanges.length - 1].overlaps(range)
    ) {
      // If the current range does not overlap with any existing ranges, we add
      // it to the list
      finalRanges.push(range)
    } else {
      // If it does overlap, then we modify the existing range to include the
      // current range as well
      finalRanges[finalRanges.length - 1] =
        finalRanges[finalRanges.length - 1].union(range)
    }
    if (range.hasNoEnd) {
      // If the current range has no end, there is nothing more that can be added to the
      // multirange
      break
    }
  }
  return finalRanges
}
